import { useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoTodoBien, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { Select } from '../../components/ui/Input';
import { Paginacion } from '../../components/ui/Paginacion';
import { StatusDot } from '../../components/ui/StatusDot';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { Criticidad, type CriticidadValor } from '../../../domain/value-objects/Criticidad';
import type { Alarma } from '../../../domain/entities/Alarma';
import { formatearFechaHora } from '../../utils/formato';
import { DetalleAlarma } from './DetalleAlarma';
import { useAlarmas, type Periodo } from './useAlarmas';
import estilos from './Alarmas.module.css';

const OPCIONES_ESTADO = [
  { valor: 'abiertas', etiqueta: 'Abiertas (activas y reconocidas)' },
  { valor: 'activa', etiqueta: 'Solo activas' },
  { valor: 'reconocida', etiqueta: 'Solo reconocidas' },
  { valor: 'resuelta', etiqueta: 'Cerradas' },
  { valor: 'todas', etiqueta: 'Todas' },
] as const;

const OPCIONES_PERIODO: { valor: Periodo; etiqueta: string }[] = [
  { valor: 'todo', etiqueta: 'Cualquier fecha' },
  { valor: '1h', etiqueta: 'Última hora' },
  { valor: '24h', etiqueta: 'Últimas 24 horas' },
  { valor: '7d', etiqueta: 'Últimos 7 días' },
  { valor: '30d', etiqueta: 'Últimos 30 días' },
];

const OPCIONES_TIPO = [
  { valor: 'analogico', etiqueta: 'Analógicos' },
  { valor: 'digital', etiqueta: 'Digitales' },
];

export function Alarmas() {
  const a = useAlarmas();
  const [detalle, setDetalle] = useState<Alarma | null>(null);

  const chips: ChipFiltro[] = [];
  if (a.cascada.seleccion.clienteId) {
    const nombre = a.cascada.clientes.find((c) => c.id === a.cascada.seleccion.clienteId)?.nombre;
    chips.push({
      clave: 'cliente',
      etiqueta: `Cliente: ${nombre ?? a.cascada.seleccion.clienteId}`,
      quitar: () => a.cascada.elegirCliente(null),
    });
  }
  if (a.cascada.seleccion.areaId) {
    const nombre = a.cascada.areas.find((x) => x.id === a.cascada.seleccion.areaId)?.nombre;
    chips.push({
      clave: 'area',
      etiqueta: `Área: ${nombre ?? a.cascada.seleccion.areaId}`,
      quitar: () => a.cascada.elegirArea(null),
    });
  }
  if (a.cascada.seleccion.nodoId) {
    const nombre = a.cascada.nodos.find((n) => n.id === a.cascada.seleccion.nodoId)?.nombre;
    chips.push({
      clave: 'nodo',
      etiqueta: `Nodo: ${nombre ?? a.cascada.seleccion.nodoId}`,
      quitar: () => a.cascada.elegirNodo(null),
    });
  }
  for (const c of a.criticidades) {
    chips.push({
      clave: `crit-${c}`,
      etiqueta: `Criticidad: ${Criticidad.from(c).etiqueta}`,
      quitar: () => a.alternarCriticidad(c),
    });
  }
  if (a.tipoUmbral) {
    chips.push({
      clave: 'tipo',
      etiqueta: `Tipo: ${a.tipoUmbral === 'analogico' ? 'Analógicos' : 'Digitales'}`,
      quitar: () => a.cambiarTipoUmbral(null),
    });
  }
  if (a.periodo !== 'todo') {
    const etiqueta = OPCIONES_PERIODO.find((p) => p.valor === a.periodo)?.etiqueta;
    chips.push({
      clave: 'periodo',
      etiqueta: `Período: ${etiqueta}`,
      quitar: () => a.cambiarPeriodo('todo'),
    });
  }

  const columnas: Columna<Alarma>[] = [
    {
      clave: 'sensor',
      encabezado: 'Sensor',
      movil: 'titulo',
      render: (al) => (
        <div className={estilos.celdaSensor}>
          <span className={estilos.nombreSensor}>{al.sensorNombre}</span>
          {al.mensaje && <span className={estilos.mensajeSensor}>{al.mensaje}</span>}
        </div>
      ),
    },
    {
      clave: 'ubicacion',
      encabezado: 'Ubicación',
      movil: 'subtitulo',
      render: (al) => al.ubicacion,
    },
    {
      clave: 'criticidad',
      encabezado: 'Criticidad',
      render: (al) => <Badge color={al.criticidad.color}>{al.criticidad.etiqueta}</Badge>,
    },
    {
      clave: 'valor',
      encabezado: 'Valor',
      alinear: 'derecha',
      render: (al) => al.valorMostrable,
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (al) => (
        <span className={estilos.estadoConCondicion} title={al.textoCondicion ?? undefined}>
          <Badge color={al.estado.color}>{al.estado.etiqueta}</Badge>
          {al.condicion === 'presente' && (
            <Icon nombre="advertencia" tamano={14} color="var(--status-danger)" />
          )}
          {al.condicion === 'normalizada' && !al.estado.esResuelta && (
            <Icon nombre="exitoContorno" tamano={14} color="var(--status-success)" />
          )}
        </span>
      ),
    },
    {
      clave: 'inicio',
      encabezado: 'Inicio',
      render: (al) => formatearFechaHora(al.fechaInicio),
    },
    {
      clave: 'duracion',
      encabezado: 'Duración',
      alinear: 'derecha',
      render: (al) => al.duracionMostrable(),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (al) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            onClick={() => setDetalle(al)}
            title="Ver detalle"
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="check"
            motivoDeshabilitado={al.motivoNoReconocible()}
            title="Reconocer"
            onClick={() => a.reconocer(al.id)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="checkDoble"
            motivoDeshabilitado={al.motivoNoResoluble()}
            title="Resolver"
            onClick={() => a.resolver(al.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Alarmas"
        descripcion="Incidencias detectadas por los sensores, en vivo."
        acciones={
          <>
            <span className={estilos.enVivo}>
              <StatusDot color="var(--status-success)" encendido tamano={8} />
              En vivo
            </span>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={a.refrescar}
              cargando={a.recargando}
            >
              Actualizar
            </Button>
          </>
        }
      />

      <div className={estilos.resumen}>
        {Criticidad.todas().map((c) => {
          const activa = a.criticidades.includes(c.valor as CriticidadValor);
          return (
            <button
              key={c.valor}
              type="button"
              className={[estilos.contador, activa ? estilos.contadorActivo : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => a.alternarCriticidad(c.valor as CriticidadValor)}
              aria-pressed={activa}
            >
              <span
                className={estilos.contadorIcono}
                style={{ background: `color-mix(in srgb, ${c.color} 15%, transparent)` }}
              >
                <Icon nombre="advertencia" tamano={20} color={c.color} />
              </span>
              <span>
                <span className={estilos.contadorValor}>{a.resumen[c.valor]}</span>
                <span className={estilos.contadorEtiqueta}>{c.etiqueta}</span>
              </span>
            </button>
          );
        })}
      </div>

      <BarraFiltros
        cascada={a.cascada}
        chips={chips}
        acciones={
          chips.length > 0 && (
            <Button variante="fantasma" icono="cerrar" onClick={a.limpiarFiltros}>
              Limpiar filtros
            </Button>
          )
        }
      >
        <Select
          etiqueta="Estado"
          value={a.estado}
          onChange={(e) => a.cambiarEstado(e.target.value as typeof a.estado)}
          opciones={OPCIONES_ESTADO.map((o) => ({ valor: o.valor, etiqueta: o.etiqueta }))}
        />
        <Select
          etiqueta="Período"
          value={a.periodo}
          onChange={(e) => a.cambiarPeriodo(e.target.value as Periodo)}
          opciones={OPCIONES_PERIODO.map((o) => ({ valor: o.valor, etiqueta: o.etiqueta }))}
        />
        <Select
          etiqueta="Tipo de sensor"
          placeholder="Todos"
          value={a.tipoUmbral ?? ''}
          onChange={(e) =>
            a.cambiarTipoUmbral((e.target.value || null) as 'analogico' | 'digital' | null)
          }
          opciones={OPCIONES_TIPO}
        />
      </BarraFiltros>

      {a.cargando ? (
        <ListaEsqueleto filas={8} />
      ) : a.error ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={a.refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : a.alarmas.length === 0 ? (
        <EstadoTodoBien
          titulo={chips.length > 0 ? 'Sin alarmas con estos filtros' : 'Todo en orden'}
          descripcion={
            chips.length > 0
              ? 'Prueba a quitar algún filtro para ver más resultados.'
              : 'No hay alarmas que atender en este momento.'
          }
          acciones={
            chips.length > 0 ? (
              <Button variante="secundario" onClick={a.limpiarFiltros}>
                Limpiar filtros
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Tabla
            datos={a.alarmas}
            columnas={columnas}
            claveFila={(al) => al.id}
            clavesResaltadas={a.recientes}
            onPulsarFila={setDetalle}
            etiqueta="Listado de alarmas"
          />
          <Paginacion
            pagina={a.pagina}
            porPagina={a.porPagina}
            total={a.total}
            onCambiar={a.cambiarPagina}
          />
        </>
      )}

      <DetalleAlarma
        alarma={detalle}
        onCerrar={() => setDetalle(null)}
        onReconocer={(id) => {
          a.reconocer(id);
          setDetalle(null);
        }}
        onResolver={(id) => {
          a.resolver(id);
          setDetalle(null);
        }}
        procesando={a.procesando}
      />
    </div>
  );
}
